import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Film, Loader2, Play, RefreshCw, Sparkles, Wand2, CheckCircle2, XCircle, Clock,
  ChevronRight, Trash2, Image as ImageIcon, Camera, ListPlus,
} from "lucide-react";
import { api, apiUrl } from "../lib/api";
import MediaThumb from "./MediaThumb";

type Status = "planned" | "rendering" | "ready" | "failed" | "animating" | "animated";

interface Shot {
  id: string;
  project_id: string;
  ordinal: number;
  title: string;
  angle: string;
  beat: string;
  prompt: string;
  negative_prompt?: string | null;
  motion_hint?: string;
  duration_seconds: number;
  status: Status;
  still_r2_uri?: string | null;
  video_r2_uri?: string | null;
  last_error?: string | null;
  prompt_id?: string | null;
  continuity_token?: string;
}

interface Project {
  id: string;
  title: string;
  scene_text: string;
  reference_description: string;
  style: string;
  aspect_ratio: string;
  shot_count: number;
  status: string;
  workflow_id: string;
  created_at: string;
  reference_uri?: string | null;
}

const STYLES = [
  { id: "cinematic",   label: "Cinematic" },
  { id: "product",     label: "Product" },
  { id: "documentary", label: "Documentary" },
  { id: "editorial",   label: "Editorial" },
  { id: "drone",       label: "Drone" },
];
const ASPECTS = ["16:9", "9:16", "1:1", "4:5"];

export default function BRollWorkspace() {
  // ── New-project form ───────────────────────────────────────────────
  const [scene, setScene] = useState("Drone roof inspection of a modern Texas luxury home at golden hour. Inspector walks the ridge line; tools, gloves, and notes visible in tight shots.");
  const [reference, setReference] = useState("A premium dark-slate roof on a contemporary Texas estate. Architectural shingles in deep charcoal, copper drip-edge, golden-hour sunlight raking across the surface. The same roof and lighting must appear in every shot.");
  const [style, setStyle] = useState<string>("cinematic");
  const [aspect, setAspect] = useState<string>("16:9");
  const [shotCount, setShotCount] = useState<number>(6);
  const [quality, setQuality] = useState<"fast" | "high">("fast");
  const [renderVideo, setRenderVideo] = useState<boolean>(true);
  const [planning, setPlanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Active project ────────────────────────────────────────────────
  const [project, setProject] = useState<Project | null>(null);
  const [shots, setShots] = useState<Shot[]>([]);
  const [rendering, setRendering] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [plannedBy, setPlannedBy] = useState<string | null>(null);

  // ── Projects list ─────────────────────────────────────────────────
  const [list, setList] = useState<Project[]>([]);

  useEffect(() => { reloadList(); }, []);

  async function reloadList() {
    try {
      const { projects } = await api.listBrollProjects();
      setList(projects ?? []);
    } catch (e: any) {
      // fail quietly — empty list is fine
    }
  }

  async function loadProject(id: string) {
    setError(null);
    try {
      const { project, shots } = await api.getBrollProject(id);
      setProject(project);
      setShots(shots);
      setWarnings([]);
    } catch (e: any) {
      setError(e?.body?.error ?? String(e));
    }
  }

  async function planNew() {
    if (!scene.trim() || !reference.trim()) {
      setError("Scene and reference are both required.");
      return;
    }
    setPlanning(true);
    setError(null);
    setWarnings([]);
    try {
      const out = await api.createBrollProject({
        sceneText: scene,
        referenceDescription: reference,
        style: style as any,
        aspectRatio: aspect as any,
        shotCount,
        quality,
        renderVideo,
      });
      const { project, shots } = await api.getBrollProject(out.projectId);
      setProject(project);
      setShots(shots);
      setWarnings(out.warnings ?? []);
      setPlannedBy(out.plannedBy ?? null);
      reloadList();
    } catch (e: any) {
      setError(e?.body?.error ?? String(e));
    } finally {
      setPlanning(false);
    }
  }

  async function renderAll() {
    if (!project) return;
    setRendering(true);
    setError(null);
    try {
      await api.renderBrollProject(project.id);
      // Mark planned/failed shots as rendering optimistically.
      setShots((prev) => prev.map((s) => s.status === "planned" || s.status === "failed" ? { ...s, status: "rendering", last_error: null } : s));
    } catch (e: any) {
      setError(e?.body?.error ?? String(e));
    } finally {
      setRendering(false);
    }
  }

  async function regenerate(shotId: string, prompt?: string) {
    if (!project) return;
    setShots((prev) => prev.map((s) => s.id === shotId ? { ...s, status: "rendering", last_error: null } : s));
    try {
      await api.regenerateBrollShot(project.id, shotId, prompt ? { prompt } : undefined);
    } catch (e: any) {
      setError(e?.body?.error ?? String(e));
    }
  }

  async function animate(shotId: string) {
    if (!project) return;
    setShots((prev) => prev.map((s) => s.id === shotId ? { ...s, status: "animating" } : s));
    try {
      await api.animateBrollShot(project.id, shotId);
    } catch (e: any) {
      setError(e?.body?.error ?? String(e));
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this B-roll project?")) return;
    try {
      await api.deleteBrollProject(id);
      if (project?.id === id) { setProject(null); setShots([]); }
      reloadList();
    } catch (e: any) {
      setError(e?.body?.error ?? String(e));
    }
  }

  // ── SSE — update shot statuses live as queue consumer reports back ─
  const esRef = useRef<EventSource | null>(null);
  const activeRef = useRef<string | null>(null);
  const projectIdRef = useRef<string | null>(null);
  useEffect(() => { activeRef.current = project?.workflow_id ?? null; }, [project?.workflow_id]);
  useEffect(() => { projectIdRef.current = project?.id ?? null; }, [project?.id]);

  // Lightweight project re-fetch (used when the queue consumer reassigns
  // prompt_ids during the still → video chain and the frontend's optimistic
  // state can't join on prompt_id alone).
  async function refreshProject() {
    const id = projectIdRef.current;
    if (!id) return;
    try {
      const { project, shots } = await api.getBrollProject(id);
      setProject(project);
      setShots(shots);
    } catch {}
  }

  useEffect(() => {
    const es = new EventSource(apiUrl("/api/events/stream"), { withCredentials: true } as EventSourceInit);
    esRef.current = es;
    es.addEventListener("schedule", (ev) => {
      try {
        const d = JSON.parse((ev as MessageEvent).data);
        const wf = activeRef.current;
        if (d.workflowId && wf && d.workflowId !== wf) return;

        if (d.kind === "generated" && Array.isArray(d.assets) && d.assets.length > 0) {
          const a = d.assets[0];
          const isVideo = (a.mediaType ?? "image") === "video";
          // Try optimistic local update first
          setShots((prev) => {
            const matched = prev.some((s) => s.prompt_id === d.promptId);
            if (!matched) {
              // Could be a re-chained video step that updated prompt_id server-side.
              // Refetch to sync.
              setTimeout(refreshProject, 300);
              return prev;
            }
            return prev.map((s) => {
              if (s.prompt_id !== d.promptId) return s;
              return isVideo
                ? { ...s, status: "animated", video_r2_uri: a.uri }
                : { ...s, status: "animating", still_r2_uri: a.uri };
            });
          });
          // After a still lands the server enqueues a video job and the shot's
          // prompt_id changes — refetch so we track the new id.
          if (!isVideo) setTimeout(refreshProject, 600);
        } else if (d.kind === "node_failed" && d.terminal && d.promptId) {
          setShots((prev) => {
            const matched = prev.some((s) => s.prompt_id === d.promptId);
            if (!matched) { setTimeout(refreshProject, 300); return prev; }
            return prev.map((s) =>
              s.prompt_id === d.promptId ? { ...s, status: "failed", last_error: d.error ?? "terminal error" } : s
            );
          });
        }
      } catch {}
    });
    return () => es.close();
  }, []);

  const readyCount = useMemo(() => shots.filter((s) => s.status === "ready" || s.status === "animated").length, [shots]);
  const failedCount = useMemo(() => shots.filter((s) => s.status === "failed").length, [shots]);

  return (
    <div className="space-y-6">
      {/* Hero / planner */}
      <section className="iiq-card-raised p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-iiq-bronze-soft border border-iiq-border-accent flex items-center justify-center">
              <Film className="w-5 h-5 text-iiq-bronze" />
            </div>
            <div>
              <h2 className="font-display font-bold text-lg text-iiq-text leading-tight">B-Roll Workspace</h2>
              <p className="text-xs text-iiq-text-muted">Runway-style shot list. One subject locked across {shotCount} angles with continuity.</p>
            </div>
          </div>
          {project && (
            <button
              onClick={() => { setProject(null); setShots([]); setWarnings([]); }}
              className="iiq-btn-ghost text-xs px-3 py-1.5 rounded-lg"
              title="Plan a new project"
            >
              <ListPlus className="w-3.5 h-3.5 mr-1.5" /> New project
            </button>
          )}
        </div>

        {!project ? (
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-iiq-text-muted">Scene</span>
                <textarea value={scene} onChange={(e) => setScene(e.target.value)} rows={4} className="iiq-input w-full mt-1 text-sm" placeholder="Describe what's happening in the world — actions, environment, time of day." />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-iiq-text-muted">Reference / continuity anchor</span>
                <textarea value={reference} onChange={(e) => setReference(e.target.value)} rows={5} className="iiq-input w-full mt-1 text-sm" placeholder="Describe the SUBJECT in detail. This text is locked into every shot prompt so the subject stays consistent (Runway calls this 'subject lock')." />
                <span className="block text-[10px] text-iiq-text-subtle mt-1">Tip: the more specific you are (materials, colors, lighting), the more consistent the multi-shot result.</span>
              </label>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-xs font-medium text-iiq-text-muted">Style</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {STYLES.map((s) => (
                    <button key={s.id} onClick={() => setStyle(s.id)} className={`px-2.5 py-1 rounded-md border text-xs ${style === s.id ? "border-iiq-border-accent bg-iiq-bronze-soft text-iiq-bronze" : "border-iiq-border bg-iiq-surface-1 text-iiq-text-muted hover:text-iiq-text"}`}>{s.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs font-medium text-iiq-text-muted">Aspect ratio</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {ASPECTS.map((a) => (
                    <button key={a} onClick={() => setAspect(a)} className={`px-2.5 py-1 rounded-md border text-xs font-mono ${aspect === a ? "border-iiq-border-accent bg-iiq-bronze-soft text-iiq-bronze" : "border-iiq-border bg-iiq-surface-1 text-iiq-text-muted hover:text-iiq-text"}`}>{a}</button>
                  ))}
                </div>
              </div>
              <label className="block">
                <span className="text-xs font-medium text-iiq-text-muted">Shot count</span>
                <input type="number" min={4} max={12} value={shotCount} onChange={(e) => setShotCount(Math.max(4, Math.min(12, Number(e.target.value) || 6)))} className="iiq-input w-24 mt-1 text-sm" />
              </label>

              <div>
                <span className="text-xs font-medium text-iiq-text-muted">Fidelity</span>
                <div className="flex gap-1.5 mt-1">
                  <button onClick={() => setQuality("fast")} className={`flex-1 px-2.5 py-1.5 rounded-md border text-xs ${quality === "fast" ? "border-iiq-border-accent bg-iiq-bronze-soft text-iiq-bronze" : "border-iiq-border bg-iiq-surface-1 text-iiq-text-muted hover:text-iiq-text"}`}>
                    <div className="font-medium">Fast</div>
                    <div className="text-[10px] opacity-70">Flux Schnell · 4 steps · ~2s</div>
                  </button>
                  <button onClick={() => setQuality("high")} className={`flex-1 px-2.5 py-1.5 rounded-md border text-xs ${quality === "high" ? "border-iiq-border-accent bg-iiq-bronze-soft text-iiq-bronze" : "border-iiq-border bg-iiq-surface-1 text-iiq-text-muted hover:text-iiq-text"}`}>
                    <div className="font-medium">High</div>
                    <div className="text-[10px] opacity-70">Flux Schnell · 8 steps · larger</div>
                  </button>
                </div>
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={renderVideo} onChange={(e) => setRenderVideo(e.target.checked)} className="mt-0.5 accent-iiq-bronze" />
                <span className="text-xs">
                  <span className="font-medium text-iiq-text">Render video clips</span>
                  <span className="block text-[10px] text-iiq-text-subtle">Each shot animates the still via Runway gen4 turbo (5s clip per shot). Uncheck for stills-only.</span>
                </span>
              </label>

              <button
                disabled={planning}
                onClick={planNew}
                className="iiq-btn-primary w-full py-2 rounded-lg text-sm flex items-center justify-center gap-2"
              >
                {planning ? <><Loader2 className="w-4 h-4 animate-spin" /> Planning shots…</> : <><Sparkles className="w-4 h-4" /> Plan {shotCount}-shot list</>}
              </button>
              {error && <div className="text-xs text-iiq-danger bg-iiq-danger/10 border border-iiq-danger/30 rounded-md px-2 py-1.5">{error}</div>}
            </div>
          </div>
        ) : (
          /* Active project header */
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-2">
              <div className="text-sm font-medium text-iiq-text">{project.title}</div>
              <div className="text-xs text-iiq-text-muted whitespace-pre-wrap line-clamp-3">{project.scene_text}</div>
              <div className="mt-2 p-2 rounded-md border border-iiq-border bg-iiq-surface-1">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-iiq-text-subtle mb-1">
                  <Camera className="w-3 h-3" /> Continuity anchor (locked in every shot)
                </div>
                <div className="text-xs text-iiq-text-muted leading-relaxed">{project.reference_description}</div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Stat label="Style" value={project.style} />
                <Stat label="Aspect" value={project.aspect_ratio} mono />
                <Stat label="Shots" value={String(project.shot_count)} />
                <Stat label="Ready" value={`${readyCount}/${shots.length}`} accent={readyCount === shots.length && shots.length > 0} />
              </div>
              <button
                disabled={rendering || shots.every((s) => s.status === "ready" || s.status === "animated" || s.status === "rendering")}
                onClick={renderAll}
                className="iiq-btn-primary w-full py-2 rounded-lg text-sm flex items-center justify-center gap-2"
              >
                {rendering ? <><Loader2 className="w-4 h-4 animate-spin" /> Dispatching…</> : <><Play className="w-4 h-4" /> Render all</>}
              </button>
              {failedCount > 0 && (
                <button onClick={renderAll} className="iiq-btn-ghost w-full py-1.5 rounded-md text-xs">
                  <RefreshCw className="w-3 h-3 mr-1" /> Retry {failedCount} failed
                </button>
              )}
            </div>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="mt-3 text-[11px] text-iiq-warning bg-iiq-warning/10 border border-iiq-warning/30 rounded-md px-2 py-1.5">
            Planner warnings: {warnings.join(" · ")}
          </div>
        )}
        {plannedBy && warnings.length === 0 && (
          <div className="mt-3 text-[10px] text-iiq-text-subtle flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 opacity-60" />
            Planned by <span className="font-mono text-iiq-text-muted">{plannedBy}</span>
          </div>
        )}
      </section>

      {/* Shot grid */}
      {project && shots.length > 0 && (
        <section>
          <div className="text-[10px] uppercase tracking-wider text-iiq-text-subtle mb-2 font-medium">Shot list · subject locked</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {shots.map((s) => (
              <ShotCard key={s.id} shot={s} aspect={project.aspect_ratio} onRegenerate={() => regenerate(s.id)} onAnimate={() => animate(s.id)} />
            ))}
          </div>
        </section>
      )}

      {/* Past projects list */}
      {list.length > 0 && (
        <section>
          <div className="text-[10px] uppercase tracking-wider text-iiq-text-subtle mb-2 font-medium">Past projects</div>
          <div className="space-y-1.5">
            {list.map((p) => (
              <div key={p.id} className={`flex items-center justify-between gap-2 rounded-md border ${project?.id === p.id ? "border-iiq-border-accent bg-iiq-bronze-soft/30" : "border-iiq-border bg-iiq-surface-1"} px-3 py-2`}>
                <button onClick={() => loadProject(p.id)} className="flex-1 text-left">
                  <div className="text-xs font-medium text-iiq-text truncate">{p.title || p.scene_text?.slice(0, 60)}</div>
                  <div className="text-[10px] text-iiq-text-subtle flex items-center gap-2 mt-0.5">
                    <span className="font-mono">{p.aspect_ratio}</span>
                    <span>·</span>
                    <span>{p.style}</span>
                    <span>·</span>
                    <span>{p.shot_count} shots</span>
                    <span>·</span>
                    <StatusPill status={p.status as any} small />
                  </div>
                </button>
                <button onClick={() => remove(p.id)} className="text-iiq-text-subtle hover:text-iiq-danger p-1" title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, mono, accent }: { label: string; value: string; mono?: boolean; accent?: boolean }) {
  return (
    <div className={`rounded-md border ${accent ? "border-iiq-border-accent bg-iiq-bronze-soft/30" : "border-iiq-border bg-iiq-surface-1"} px-2 py-1.5`}>
      <div className="text-[9px] uppercase tracking-wider text-iiq-text-subtle">{label}</div>
      <div className={`text-xs ${accent ? "text-iiq-bronze font-semibold" : "text-iiq-text"} ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function ShotCard({ shot, aspect, onRegenerate, onAnimate }: {
  shot: Shot; aspect: string;
  onRegenerate: () => void; onAnimate: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const aspectClass =
    aspect === "9:16" ? "aspect-[9/16]" :
    aspect === "1:1"  ? "aspect-square" :
    aspect === "4:5"  ? "aspect-[4/5]"  :
    "aspect-video";

  return (
    <div className="iiq-card-raised overflow-hidden">
      <div className={`${aspectClass} bg-iiq-surface-2 relative flex items-center justify-center`}>
        {shot.video_r2_uri ? (
          <MediaThumb url={shot.video_r2_uri} mime="video/mp4" alt={shot.title} />
        ) : shot.still_r2_uri ? (
          <MediaThumb url={shot.still_r2_uri} mime="image/png" alt={shot.title} />
        ) : shot.status === "rendering" ? (
          <div className="flex flex-col items-center gap-2 text-iiq-text-muted">
            <Loader2 className="w-6 h-6 animate-spin text-iiq-bronze" />
            <div className="text-[10px] uppercase tracking-wider">Rendering</div>
          </div>
        ) : shot.status === "animating" ? (
          <div className="flex flex-col items-center gap-2 text-iiq-text-muted">
            <Loader2 className="w-6 h-6 animate-spin text-iiq-bronze" />
            <div className="text-[10px] uppercase tracking-wider">Animating</div>
          </div>
        ) : shot.status === "failed" ? (
          <div className="flex flex-col items-center gap-1 text-iiq-danger px-3 text-center">
            <XCircle className="w-6 h-6" />
            <div className="text-[10px]">{shot.last_error?.slice(0, 80) ?? "failed"}</div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-iiq-text-subtle">
            <ImageIcon className="w-6 h-6 opacity-50" />
            <div className="text-[10px] uppercase tracking-wider">Planned</div>
          </div>
        )}
        <div className="absolute top-1.5 left-1.5">
          <StatusPill status={shot.status} />
        </div>
        <div className="absolute top-1.5 right-1.5 text-[9px] font-mono px-1.5 py-0.5 rounded bg-black/60 text-iiq-bronze">
          #{shot.ordinal}
        </div>
      </div>

      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-iiq-text truncate">{shot.title}</div>
            <div className="flex items-center gap-1.5 mt-0.5 text-[10px]">
              <span className="px-1.5 py-0.5 rounded bg-iiq-surface-2 border border-iiq-border text-iiq-text-muted">{shot.angle.replace("_", " ")}</span>
              <span className="px-1.5 py-0.5 rounded bg-iiq-surface-2 border border-iiq-border text-iiq-text-muted">{shot.beat}</span>
              <span className="text-iiq-text-subtle font-mono">{shot.duration_seconds}s</span>
            </div>
          </div>
          <button onClick={() => setExpanded((e) => !e)} className="text-iiq-text-subtle hover:text-iiq-text p-1">
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </button>
        </div>

        {expanded && (
          <div className="text-[11px] text-iiq-text-muted leading-relaxed space-y-1 border-t border-iiq-border pt-2">
            <div><span className="text-iiq-text-subtle">prompt:</span> {shot.prompt}</div>
            {shot.motion_hint && <div><span className="text-iiq-text-subtle">motion:</span> {shot.motion_hint}</div>}
            {shot.last_error && <div className="text-iiq-danger"><span className="text-iiq-text-subtle">error:</span> {shot.last_error}</div>}
          </div>
        )}

        <div className="flex items-center gap-1.5 pt-1">
          <button
            disabled={shot.status === "rendering" || shot.status === "animating"}
            onClick={onRegenerate}
            className="iiq-btn-ghost text-[11px] px-2 py-1 rounded-md flex items-center gap-1"
            title="Regenerate this shot"
          >
            <RefreshCw className="w-3 h-3" /> Regen
          </button>
          <button
            disabled={!shot.still_r2_uri || shot.status === "animating" || shot.status === "animated"}
            onClick={onAnimate}
            className="iiq-btn-ghost text-[11px] px-2 py-1 rounded-md flex items-center gap-1"
            title="Animate via Runway gen4"
          >
            <Wand2 className="w-3 h-3" /> Animate
          </button>
          {shot.still_r2_uri && (
            <a href={shot.still_r2_uri} target="_blank" rel="noreferrer" className="iiq-btn-ghost text-[11px] px-2 py-1 rounded-md ml-auto" title="Open in new tab">
              Open ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, small }: { status: Status | string; small?: boolean }) {
  const cls = small ? "text-[9px] px-1.5 py-0.5" : "text-[10px] px-2 py-0.5";
  switch (status) {
    case "ready":      return <span className={`${cls} rounded font-medium bg-iiq-success/20 text-iiq-success border border-iiq-success/40 flex items-center gap-1`}><CheckCircle2 className="w-2.5 h-2.5" /> ready</span>;
    case "animated":   return <span className={`${cls} rounded font-medium bg-iiq-info/20 text-iiq-info border border-iiq-info/40`}>animated</span>;
    case "rendering":  return <span className={`${cls} rounded font-medium bg-iiq-warning/20 text-iiq-warning border border-iiq-warning/40 flex items-center gap-1`}><Clock className="w-2.5 h-2.5" /> rendering</span>;
    case "animating":  return <span className={`${cls} rounded font-medium bg-iiq-warning/20 text-iiq-warning border border-iiq-warning/40 flex items-center gap-1`}><Clock className="w-2.5 h-2.5" /> animating</span>;
    case "failed":     return <span className={`${cls} rounded font-medium bg-iiq-danger/20 text-iiq-danger border border-iiq-danger/40 flex items-center gap-1`}><XCircle className="w-2.5 h-2.5" /> failed</span>;
    default:           return <span className={`${cls} rounded font-medium bg-iiq-surface-2 text-iiq-text-subtle border border-iiq-border`}>{status}</span>;
  }
}
